/**
 * Las secciones de la agencia: lo que cuelga del desplegable de la agencia (el ícono del pie de
 * `SidebarV2`), en UNA sola lista (tanda «Agencia 2.1», 04/10, contrato §1).
 *
 * De acá salen TRES cosas, y por eso no pueden escribirse en otro lado:
 * - el desplegable de la agencia (`agencyItems` de `SidebarV2`), con sus grupos;
 * - las solapas de la agencia en la 2.1 (`AgencyTabsV2`), en el MISMO orden;
 * - `AGENCY_PAGE_PREFIXES`, las DOS señales de «estás acá» fuera de un cliente: el prefijo
 *   «Agencia ·» de la miga (`PAGE_GROUPS` de `TopBarV2`) y el botón de la agencia marcado en la
 *   barra.
 * Escritas en varias listas, tarde o temprano una pantalla se anunciaría en la miga y la barra no
 * la marcaría, o al revés: es lo que pasó al borrar «Herramientas» (02/10), cuando Alertas,
 * Métricas y Planillas quedaron sin ninguna marca en la barra (review C-rec-fe-menu-02).
 *
 * Cada sección lleva:
 * - `group`: `data` («Datos») y `team` («Equipo y ajustes») son los grupos del desplegable; `plan`
 *   va al pie, como la tarjeta del plan;
 * - `activeOn`: los prefijos de ruta en los que la sección queda marcada. Un prefijo matchea la
 *   ruta exacta o cualquier subruta: `/agency/conections` cubre también `/agency/conections/export`.
 *
 * «Administración de cuentas» (`/manage-accounts`, la administración MCC, ex «Cuentas vinculadas») ya
 * no está: no se usa más (fede, 04/10). Era la única sección con gate de módulo, así que la lista se
 * ofrece entera a todas las agencias.
 *
 * Los íconos no viven acá: los mapea `SidebarV2` por `key`.
 */
export const AGENCY_GROUP_DATA = 'data';
export const AGENCY_GROUP_TEAM = 'team';
export const AGENCY_GROUP_PLAN = 'plan';

// Destinos que otras pantallas linkean por su nombre (Suscripción › «Lo que usas de tu plan»):
// Conexiones › Cuentas y el uso del portal de clientes.
export const AGENCY_ACCOUNTS_PATH = '/agency/ad-accounts';
export const AGENCY_PORTAL_USAGE_PATH = '/agency/portal-usage';

export const AGENCY_SECTIONS = [
    // Conexiones abarca sus tres pestañas de la 2.1 (D1): Plataformas, Cuentas —`/agency/ad-accounts`
    // re-enmarcada, que hasta el 04/10 marcaba «Cuentas vinculadas»— y Exportación, que incluye la
    // exportación a planillas.
    {key: 'connections', to: '/agency/conections', labelKey: 'sidebar_v2.connections', group: AGENCY_GROUP_DATA,
        activeOn: ['/agency/conections', AGENCY_ACCOUNTS_PATH, '/sheets-sync']},
    // Las dos solapas de Personalización: métricas personalizadas y comparativas.
    {key: 'metrics', to: '/agency/personalization/metrics', labelKey: 'personalization.tabs.metrics',
        group: AGENCY_GROUP_DATA, activeOn: ['/agency/personalization']},
    {key: 'team', to: '/agency-team', labelKey: 'sidebar_v2.team', group: AGENCY_GROUP_TEAM,
        activeOn: ['/agency-team']},
    {key: 'alerts', to: '/agency/alerts-configuration', labelKey: 'sidebar_v2.alerts', group: AGENCY_GROUP_TEAM,
        activeOn: ['/agency/alerts-configuration']},
    {key: 'profile', to: '/agency/profile', labelKey: 'sidebar_v2.agency_profile', group: AGENCY_GROUP_TEAM,
        activeOn: ['/agency/profile']},
    {key: 'subscription', to: '/agency/subscription', labelKey: 'sidebar_v2.subscription', group: AGENCY_GROUP_PLAN,
        activeOn: ['/agency/subscription']},
];

// Con el Centro de Alertas (tanda «Centro de Alertas», F3), «Alertas» de la agencia lleva a «Mis reglas»
// del Centro, que reemplaza a la configuración de alertas en la 2.1 (`/agency/alerts-configuration`
// redirige ahí). Se escribe acá y no se importa de `alerts_center/`: este módulo es de datos.
export const ALERTS_CENTER_RULES_PATH = '/alerts/rules';

/**
 * Las secciones como las ve ESTA agencia: con el Centro de Alertas, «Alertas» apunta a «Mis reglas».
 * Sigue marcándose en su ruta de siempre (`activeOn`), que con el Centro redirige: adentro del Centro
 * lo marca su propia entrada de la barra, que es de primer nivel, y no la agencia.
 */
export function agencySectionsFor({alertsCenter = false} = {}) {
    if (!alertsCenter) return AGENCY_SECTIONS;
    return AGENCY_SECTIONS.map((section) => (section.key === 'alerts'
        ? {...section, to: ALERTS_CENTER_RULES_PATH}
        : section));
}

export const AGENCY_PAGE_PREFIXES = [...new Set(AGENCY_SECTIONS.flatMap((section) => section.activeOn))];

export function matchesPrefix(pathname, prefix) {
    return pathname === prefix || pathname.startsWith(prefix + '/');
}

export function isAgencyPage(pathname) {
    return AGENCY_PAGE_PREFIXES.some((prefix) => matchesPrefix(pathname || '', prefix));
}

export function isSectionActive(section, pathname) {
    return section.activeOn.some((prefix) => matchesPrefix(pathname || '', prefix));
}
